import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { updatePortalProfile } from "../../lib/api";
import { PortalChangePassword } from "./PortalChangePassword";

export default function PortalProfile({ me, onUpdated }) {
  const [form, setForm] = useState({
    name: me.name || "",
    phone: me.phone || "",
    title: me.title || "",
    organization: me.organization || "",
    address: me.address || "",
  });
  const [saving, setSaving] = useState(false);
  const [digest, setDigest] = useState(!!me.digest_enabled);
  const isTransport = me.role === "Transportation";

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleDigest = async (val) => {
    setDigest(val);
    try {
      const res = await updatePortalProfile({ digest_enabled: val });
      toast.success(val ? "Daily summary turned on" : "Instant alerts turned on");
      onUpdated && onUpdated({ ...res.data, is_portal: true });
    } catch (err) {
      setDigest(!val);
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updatePortalProfile(form);
      toast.success("Contact info updated");
      onUpdated && onUpdated({ ...res.data, is_portal: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="portal-profile">
      <Card>
        <CardHeader><CardTitle className="text-lg">My Contact Information</CardTitle></CardHeader>
        <CardContent className="space-y-4 max-w-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-md bg-slate-50 border p-3">
              <p className="text-xs text-muted-foreground">Login Email</p>
              <p className="font-medium" data-testid="portal-profile-email">{me.email}</p>
            </div>
            <div className="rounded-md bg-slate-50 border p-3">
              <p className="text-xs text-muted-foreground">{me.role === "Transportation" ? "Transportation Provider" : "District"}</p>
              <p className="font-medium" data-testid="portal-profile-district">{me.role === "Transportation" ? me.provider : me.district}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="portal-profile-name" />
            </div>
            <div className="space-y-2">
              <Label>Title / Role</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Special Ed Director" data-testid="portal-profile-title" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="portal-profile-phone" />
            </div>
            <div className="space-y-2">
              <Label>Organization</Label>
              <Input value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="portal-profile-org" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Mailing Address</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="portal-profile-address" />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="portal-profile-save">
            {saving ? "Saving..." : "Save Contact Info"}
          </Button>
        </CardContent>
      </Card>

      {isTransport && (
        <Card data-testid="portal-digest-card">
          <CardHeader><CardTitle className="text-lg">Absence Notifications</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4 max-w-2xl">
              <div>
                <p className="font-medium">Daily summary email</p>
                <p className="text-sm text-muted-foreground">
                  Get ONE morning email (around 10:45 AM) listing all your absent / early students that day,
                  instead of a separate email for each student.
                </p>
              </div>
              <Switch checked={digest} onCheckedChange={toggleDigest} data-testid="portal-digest-toggle" />
            </div>
          </CardContent>
        </Card>
      )}

      <PortalChangePassword />
    </div>
  );
}
